import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/Badge';

export interface Breadcrumb {
  label: string;
  /** Omit on the final crumb - the page you are already on is not a link. */
  to?: string;
}

export interface PageHeaderProps {
  title: string;
  rightSlot?: ReactNode;
  /** Trail above the title. The last entry should have no `to`. */
  breadcrumb?: Breadcrumb[];
  /** Result count shown beside the title (e.g. total rows in a console). */
  count?: number;
  /**
   * Pins the header to the top of its scroll container. Needs an opaque
   * background, which this already has, or content scrolls through it.
   */
  sticky?: boolean;
  /**
   * Tighter vertical rhythm and a smaller title, for console screens where the
   * heading competes with the data for space. Member screens leave this off and
   * keep the H1 their Figma designs specify.
   */
  dense?: boolean;
  /** Override the <h1> className when a screen's Figma specifies a size outside the dense/default pair. */
  titleClassName?: string;
  /**
   * Show the breadcrumb trail only: the title stays as a screen-reader-only
   * <h1> and the trail keeps its final crumb, so the page is named once.
   */
  hideTitle?: boolean;
}

/** Sub-navbar page header bar (e.g. "Create Startup Profile", "Users"). */
export function PageHeader({
  title,
  rightSlot,
  breadcrumb,
  count,
  sticky = false,
  dense = false,
  titleClassName,
  hideTitle = false,
}: PageHeaderProps) {
  // `bg-surface-card` resolves to #FFFFFF in light, exactly the `bg-white` it
  // replaced, so member screens (CreateStartupPage) render unchanged.
  // Dense min-height = py-3 + a 42px admin header button + the 1px border, so a
  // console with no header actions (Dashboard, Pages) is as tall as one with
  // them, instead of a shorter bar that jumps when switching consoles.
  const barClass = `border-border-subtle bg-surface-card text-text-primary flex items-center justify-between gap-4 border-b px-6 sm:px-10 ${
    dense ? 'min-h-[67px] py-3' : 'py-4'
  } ${sticky ? 'sticky top-0 z-20' : ''}`.trim();

  // Dense puts the trail and title on one row, so a final crumb naming the
  // current page reads as a repeat ("Admin / Investors Investors", or the
  // near-repeat "Admin / Partners Trusted Partners"). The h1 already names the
  // current page; drop that unlinked crumb and keep a trailing separator into
  // the title. Linked ancestor crumbs always stay.
  const currentDropped = dense && !hideTitle && !!breadcrumb?.length && !breadcrumb.at(-1)?.to;
  const trail = currentDropped ? breadcrumb.slice(0, -1) : breadcrumb;

  const crumbs =
    trail && trail.length > 0 ? (
      <nav aria-label="Breadcrumb">
        <ol className="text-text-secondary text-p6 flex items-center gap-1.5">
          {trail.map((crumb, index) => (
            <li key={crumb.label} className="flex items-center gap-1.5">
              {index > 0 && <span aria-hidden="true">/</span>}
              {crumb.to ? (
                <Link
                  to={crumb.to}
                  className="hover:text-text-primary focus-visible:ring-accent cursor-pointer transition-colors focus-visible:ring-2 focus-visible:outline-none"
                >
                  {crumb.label}
                </Link>
              ) : (
                <span aria-current="page">{crumb.label}</span>
              )}
              {currentDropped && index === trail.length - 1 && <span aria-hidden="true">/</span>}
            </li>
          ))}
        </ol>
      </nav>
    ) : null;

  return (
    <div className={barClass}>
      <div className={dense ? 'flex flex-wrap items-baseline gap-x-3 gap-y-1' : 'flex flex-col gap-1'}>
        {/* Non-dense: trail stacked above title (conventional reading order).
            Dense: trail before title on the same baseline row — navigation
            context leads into the heading. */}
        {crumbs}
        <div className="flex items-center gap-3">
          <h1
            className={
              hideTitle
                ? 'sr-only'
                : (titleClassName ??
                  (dense ? 'font-sans text-h3' : 'font-sans text-[20px] font-bold leading-[30px] text-navy'))
            }
          >
            {title}
          </h1>
          {count !== undefined && (
            <Badge variant="neutral" size="count">
              {count}
            </Badge>
          )}
        </div>
      </div>
      {rightSlot}
    </div>
  );
}
